import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import { COMMAND_PRIORITY_LOW, KEY_ESCAPE_COMMAND } from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormulaFunctionName } from '../../formula-engine';
import { $enclosingCallsAtCaret, EnclosingCall } from './enclosingCalls';
import { activeParamIndex, renderFunctionDetail } from './FunctionTypeaheadPlugin';
import { caretRect, DocPopup } from './tokenDocs';

/** How long the caret sits in an argument before that argument's signature shows. */
export const SIGNATURE_HINT_DELAY_MS = 400;

export interface SignatureHint {
  fn: FormulaFunctionName;
  /** 0-based index of the argument the caret is in. */
  argIndex: number;
  /** How deep the call is nested — tells MAX(MAX(… from MAX(… apart. */
  depth: number;
}

/**
 * What to hint at the caret: the innermost call it sits in, or nothing outside
 * one. The same read the typeahead menus and comma completion work from, so
 * the hint always describes the call whose arguments they are completing.
 */
export function resolveSignatureHint(calls: EnclosingCall[]): SignatureHint | null {
  const call = calls[0];
  return call ? { fn: call.fn, argIndex: call.argIndex, depth: calls.length } : null;
}

/** Identity of a hint: while this holds steady the popup is left exactly as it is. */
export function signatureHintKey(hint: SignatureHint | null): string | null {
  return hint && `${hint.fn}:${hint.argIndex}:${hint.depth}`;
}

interface SignatureHintPluginProps {
  /** While true (a typeahead or hint menu is open), the hint stays hidden. */
  suppressed?: boolean;
}

/**
 * IDE-style parameter hints: inside a call, the function's signature shows
 * under the caret with the argument being typed picked out. The delay gates
 * opening only — once up it follows the caret from argument to argument, and
 * closes when the caret leaves the call.
 */
export function SignatureHintPlugin({ suppressed = false }: SignatureHintPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [popup, setPopup] = useState<{ hint: SignatureHint; rect: DOMRect } | null>(null);
  const popupRef = useRef(popup);
  popupRef.current = popup;
  const timerRef = useRef<number | undefined>(undefined);
  /** The hint the timer is counting down for: typing on within the same argument must not restart it. */
  const pendingRef = useRef<string | null>(null);
  /** The hint Escape dismissed: it stays gone until the caret reaches another argument. */
  const dismissedRef = useRef<string | null>(null);
  const suppressedRef = useRef(suppressed);
  suppressedRef.current = suppressed;

  const hide = useCallback(() => {
    window.clearTimeout(timerRef.current);
    pendingRef.current = null;
    setPopup(null);
  }, []);

  const evaluate = useCallback(() => {
    if (suppressedRef.current) {
      return;
    }
    const hint = editor.getEditorState().read(() => resolveSignatureHint($enclosingCallsAtCaret()));
    const key = signatureHintKey(hint);
    if (key !== dismissedRef.current) {
      dismissedRef.current = null;
    }
    if (!hint || key === dismissedRef.current) {
      hide();
      return;
    }
    if (key === signatureHintKey(popupRef.current?.hint ?? null)) {
      // Same argument as the popup already shows: leave it where it is, so it
      // holds still while its argument is typed out.
      return;
    }
    if (popupRef.current) {
      // Already open — the next argument follows the caret without a wait.
      window.clearTimeout(timerRef.current);
      pendingRef.current = null;
      const rect = caretRect(editor.getRootElement());
      if (rect) {
        setPopup({ hint, rect });
      }
      return;
    }
    if (key === pendingRef.current) {
      // Still waiting on this same argument; typing does not reset the wait.
      return;
    }
    window.clearTimeout(timerRef.current);
    pendingRef.current = key;
    timerRef.current = window.setTimeout(() => {
      pendingRef.current = null;
      const rect = caretRect(editor.getRootElement());
      if (rect) {
        setPopup({ hint, rect });
      }
    }, SIGNATURE_HINT_DELAY_MS);
  }, [editor, hide]);

  useEffect(() => {
    const onBlur = () => hide();
    return mergeRegister(
      // Selection-only updates fire this too, so clicking into a stored
      // formula hints without a listener of its own.
      editor.registerUpdateListener(evaluate),
      editor.registerCommand<KeyboardEvent>(
        KEY_ESCAPE_COMMAND,
        (event) => {
          if (!popupRef.current) {
            return false;
          }
          // Consumed, so the first Escape only dismisses the hint — a second
          // one still reaches the dialog around the editor.
          event.preventDefault();
          dismissedRef.current = signatureHintKey(popupRef.current.hint);
          hide();
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
      // The root element may mount after this effect (or be swapped); attach
      // wherever it currently is. The teardown call (null root) detaches.
      editor.registerRootListener((rootElement, prevRootElement) => {
        prevRootElement?.removeEventListener('blur', onBlur);
        // eslint-disable-next-line @eslint-react/web-api/no-leaked-event-listener -- unregister()'s teardown calls back with (null, root), removing this
        rootElement?.addEventListener('blur', onBlur);
      }),
      () => window.clearTimeout(timerRef.current)
    );
  }, [editor, evaluate, hide]);

  useEffect(() => {
    if (suppressed) {
      hide();
    } else {
      // A menu just closed and no edit need follow it (picking a chip closes
      // the menu after its own update), so re-read rather than wait.
      evaluate();
    }
  }, [suppressed, evaluate, hide]);

  if (!popup) {
    return null;
  }
  return (
    <DocPopup rect={popup.rect}>
      {renderFunctionDetail(popup.hint.fn, activeParamIndex(popup.hint.fn, popup.hint.argIndex))}
    </DocPopup>
  );
}
